import { Component, OnInit, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UniversalSearchService } from '../services/universal-search.service';
import { DateTime } from 'luxon';
import { SessionStorageService } from '../../services/session-storage.service';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { MatRadioModule } from '@angular/material/radio';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { HierarchyModalComponent } from '../hierarchy-modal/hierarchy-modal.component';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';

@Component({
  selector: 'app-search-modal',
  templateUrl: './search-modal.component.html',
  styleUrls: ['./search-modal.component.scss'],
  imports: [
    MatSelectModule,
    FormsModule,
    MatRadioModule,
    MatDatepickerModule,
    HierarchyModalComponent,
    MatButtonModule,
    MatInputModule,
  ],
  providers: [],
})
export class SearchModalComponent implements OnInit {
  private dialogRef = inject<MatDialogRef<SearchModalComponent>>(MatDialogRef);
  data = inject(MAT_DIALOG_DATA);
  private usService = inject(UniversalSearchService);
  private ss = inject(SessionStorageService);

  headline;
  searchModel = {
    firstName: null,
    lastName: null,
    oppNum: 3,
    relThresh: 65,
    actionDate: '30',
    suggestions: [],
    actions: [],
    postalCode: null,
    city: null,
    ageFrom: null,
    ageTo: null,
    incomeFrom: null,
    incomeTo: null,
    assetsFrom: null,
    assetsTo: null,
    productsFrom: null,
    productsTo: null,
    profileFrom: null,
    profileTo: null,
    source: null,
    marketCategory: {
      youngConsumers: null,
      preRetirees: null,
      retirees: null,
      smallBusiness: null,
      professionals: null,
      others: null,
    },
    additional: null,
    timeSensitive: 'all',
    callBackStart: null,
    callBackEnd: null,
    callBackFilter: 'all',
  };

  savedSearch;
  searchType;
  searchDef;
  suggestions;
  selectedSuggestions = {};
  selectedActions = { '2100': true, '2300': true, '2200': true, '2900': true };
  hierarchyData: any;
  rights: string[];
  displayHierarchyFields: boolean;

  ngOnInit() {
    this.searchType = this.data.type;
    this.rights = this.ss.get('rights');
    if (this.rights) {
      this.displayHierarchyFields =
        this.rights.includes('DisplayAndSearchOnRepID') &&
        this.rights.includes('DisplayAndSearchOnUnitId') &&
        this.rights.includes('DisplayAndSearchOnUserId');
    }

    if (this.data.saved && !this.data.saved.searchDef) {
      this.savedSearch = this.data.saved;
    } else if (
      this.data.saved &&
      JSON.parse(JSON.parse(JSON.stringify(this.data.saved)).searchDef)
    ) {
      this.savedSearch = JSON.parse(
        JSON.parse(JSON.stringify(this.data.saved)).searchDef
      );
    }

    switch (this.searchType) {
      case 'name':
        this.headline = 'Search by Name';
        break;

      case 'opp':
        this.headline = 'Search by Best Opportunities';
        break;

      case 'suggestion': {
        this.headline = 'Search by Suggestions';
        const bridgesObj =
          this.savedSearch && this.savedSearch.search
            ? this.savedSearch.search.find(el =>
                Object.keys(el).includes('bridgeids')
              )
            : null;
        this.getSuggs(bridgesObj);
        break;
      }

      case 'lastAction':
        this.headline = 'Search by Last Action';
        break;

      case 'timeSensitive':
        this.headline = 'Search by Time Sensitive Suggestions';
        break;

      case 'demographics':
        this.headline = 'Search by Demographics';
        break;

      case 'callBack':
        this.headline = 'Search by Callback Date';
        break;

      default:
        this.headline = 'Search';
        break;
    }
  }

  getSuggs(saved) {
    this.usService.getSuggestions().subscribe(x => {
      this.suggestions = x;
      if (saved && saved.bridgeids) {
        saved.bridgeids.split(',').forEach(el => {
          this.selectedSuggestions[el] = true;
        });
      }
    });
  }

  setHierarchyData(hierarchyData: any) {
    this.hierarchyData = hierarchyData;
  }

  cancel() {
    this.dialogRef.close({ data: 'cancel' });
  }

  search() {
    if (this.hierarchyData) {
      this.searchDef['hierarchyData'] = this.hierarchyData.data;
    }
    this.dialogRef.close({ data: this.searchDef });
    this.dialogRef.afterClosed().subscribe(() => {
      this.dialogRef = null;
    });
  }

  setSearchData() {
    switch (this.searchType) {
      case 'name':
        this.searchDef = {
          firstName: this.searchModel.firstName,
          lastName: this.searchModel.lastName,
          searchby: 'name',
        };
        this.search();
        break;

      case 'opp':
        this.searchDef = {
          RelevantBridgeCount: this.searchModel.oppNum,
          searchby: 'RelevantBridgeCount',
        };
        this.search();
        break;

      // case 'highScore':
      //   this.searchDef = {searchby: 'highbridge'};
      //   break;

      // case 'callList':
      //   this.searchDef = {searchby: 'callList'};
      //   break;

      case 'suggestion': {
        const suggs = Object.keys(this.selectedSuggestions).filter(
          x => this.selectedSuggestions[x]
        );

        this.searchDef = {
          searchBy: 'suggestion',
          bridgeids: suggs,
          relThresh: this.searchModel.relThresh * 10,
        };
        this.search();
        break;
      }
      case 'lastAction': {
        if (this.selectedActions['2100']) {
          const follows = ['2120', '2128', '2150', '2155', '2160'];
          follows.forEach(x => {
            this.selectedActions[x] = true;
          });
        }

        if (this.selectedActions['2300']) {
          const likes = ['2300', '2310', '2320', '2330', '2335', '2390'];
          likes.forEach(x => {
            this.selectedActions[x] = true;
          });
        }

        if (this.selectedActions['2200']) {
          const noThanks = [
            '2210',
            '2220',
            '2230',
            '2240',
            '2245',
            '2250',
            '2290',
          ];
          noThanks.forEach(x => {
            this.selectedActions[x] = true;
          });
        }

        let actions = '';
        Object.keys(this.selectedActions).forEach(el => {
          if (this.selectedActions[el] && actions.length > 0) {
            actions += `,${el}`;
          } else if (this.selectedActions[el]) {
            actions += `${el}`;
          }
        });
        const date = DateTime.now().minus({ days: 30 }).toFormat('LL/dd/yyyy');
        this.searchDef = {
          searchBy: 'action',
          actionids: actions,
          actionStartDate: date,
        };
        if (!this.searchDef.actionids) {
          this.searchDef = 'cancel';
        }
        this.search();
        break;
      }
      case 'timeSensitive': {
        this.searchDef = { searchBy: 'TimeSensitive' };
        if (this.searchModel.timeSensitive !== 'all') {
          this.searchDef['prospects'] =
            this.searchModel.timeSensitive === 'prospects';
          this.search();
        } else {
          this.search();
        }

        break;
      }
      case 'demographics': {
        this.searchDef = { searchBy: 'Demographics', ...this.searchModel };
        let temp = '';
        Object.keys(this.searchDef.marketCategory).forEach(el => {
          if (this.searchDef.marketCategory[el]) {
            if (temp && temp.length > 0) {
              temp += `,${el}`;
            } else {
              temp += `${el}`;
            }
          }
        });
        Object.keys(this.searchDef).forEach(el => {
          this.searchDef[el] = !this.searchDef[el] ? '' : this.searchDef[el];
        });
        this.searchDef.marketCategory = temp;
        this.search();

        break;
      }
      case 'callBack': {
        const start = DateTime.fromJSDate(
          new Date(this.searchModel.callBackStart)
        ).toFormat('LL/dd/yyyy');
        const end = DateTime.fromJSDate(
          new Date(this.searchModel.callBackEnd)
        ).toFormat('LL/dd/yyyy');

        this.searchDef = { searchBy: 'action', StartDate: start, EndDate: end };
        if (this.searchModel.callBackFilter !== 'all') {
          this.searchDef['prospects'] =
            this.searchModel.callBackFilter === 'prospects';
          this.search();
        } else {
          this.search();
        }

        break;
      }
      default:
        break;
    }
  }

  toggleAllSuggs(ev) {
    Object.keys(this.suggestions).forEach(el => {
      this.suggestions[el].forEach(x => {
        this.selectedSuggestions[x.id] = ev.checked;
      });
    });
  }

  isDisabled() {
    let btnDisabled = false;
    switch (this.searchType) {
      case 'suggestion': {
        const suggs = Object.keys(this.selectedSuggestions).filter(
          x => this.selectedSuggestions[x]
        );
        btnDisabled = suggs.length < 1;
        break;
      }

      case 'callBack':
        btnDisabled =
          !this.searchModel.callBackEnd || !this.searchModel.callBackStart;
        break;

      default:
        btnDisabled = false;
    }

    //The following code requires hierarchy search (if available) along with items selected.
    //Might be needed in the future.
    //
    // btnDisabled =
    //   btnDisabled || (this.displayHierarchyFields && !this.hierarchyData);

    return btnDisabled;
  }
}
